<template>
    <section
        :id="slice.primary.anchorName || undefined"
        class="slice slice-content-grid slice-content-grid--job-categories"
        aria-label="content-grid-job-categories"
        data-testid="slice-content-grid-job-categories"
        :data-mui-theme="theme"
    >
        <div class="slice-content">
            <div class="slice-content-grid--job-categories__layout">
                <div>
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.title)"
                        :field="slice.primary.title"
                        class="mui-title"
                        data-testid="slice-content-grid-job-categories-title"
                    />
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.description)"
                        :field="slice.primary.description"
                        class="slice-content-grid--job-categories__description"
                        data-testid="slice-content-grid-job-categories-description"
                    />
                </div>

                <div class="slice-content-grid--job-categories__grid" data-testid="slice-content-grid-job-categories-grid">
                    <div
                        v-for="(item, idx) in slice.primary.items"
                        :key="idx"
                        class="slice-content-grid--job-categories__card"
                        :data-testid="`slice-content-grid-job-categories-card--${idx}`"
                    >
                        <div
                            v-if="isFilled.image(item.image)"
                            class="slice-content-grid--job-categories__card-illustration mui-background"
                            :data-testid="`slice-content-grid-job-categories-card-image--${idx}`"
                        >
                            <PrismicImage :field="item.image" loading="lazy" sizes="200px" height="70px" :alt="item.image.alt ?? ''" />
                        </div>
                        <h3
                            v-if="isFilled.keyText(item.title)"
                            class="slice-content-grid--job-categories__card-title"
                            :data-testid="`slice-content-grid-job-categories-card-title--${idx}`"
                        >
                            {{ item.title }}
                        </h3>
                        <PrismicRichText
                            v-if="isFilled.richText(item.text)"
                            :field="item.text"
                            class="slice-content-grid--job-categories__card-jobs"
                            :data-testid="`slice-content-grid-job-categories-card-jobs--${idx}`"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {ContentGridSlice} from '@prismicio-types';

    type ContentGridJobCategoriesInZone = Extract<ContentGridSlice, {variation: 'contentGridJobCategories'}>;
    const props = defineProps(getSliceComponentProps<ContentGridJobCategoriesInZone>());

    const theme = computed(() => props.slice.primary.theme);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-content-grid--job-categories {
        .slice-content {
            text-align: left;
        }

        &__layout {
            display: grid;
            gap: var(--joy-core-spacing-8);
            grid-template-columns: 1fr;

            @include mq.screen_lg(min) {
                grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.7fr);
                gap: var(--joy-core-spacing-12);
            }
        }

        &__description {
            @include text.getFontBySize(primary-400);
            margin-top: var(--joy-core-spacing-4);
        }

        &__grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: var(--joy-core-spacing-4);

            @include mq.screen_xs(min) {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: var(--joy-core-spacing-8);
            }

            @include mq.screen_sm(min) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
            }
        }

        &__card {
            min-width: 0;
            text-align: center;
            border-radius: 20px;
            padding: var(--joy-core-spacing-4);
            padding-bottom: var(--joy-core-spacing-4);
            background-color: var(--mui-color-background);

            @include mq.screen_xs(min) {
                display: grid;
                align-items: flex-start;
                grid-template-rows: subgrid;
                gap: 0;
                grid-row: span 3;
                background-color: transparent;
                padding: 0;
            }
        }

        &__card-illustration {
            display: none;
            @include mq.screen_xs(min) {
                display: flex;
                justify-content: center;
                width: 100%;
                margin-bottom: var(--joy-core-spacing-9);
                padding: var(--joy-core-spacing-4) var(--joy-core-spacing-3) 0;
                border-radius: 20px;
            }

            img {
                height: 70px;
                display: none;
                @include mq.screen_xs(min) {
                    display: block;
                    width: 100%;
                    max-width: 140px;
                    object-fit: contain;
                    transform: translateY(var(--joy-core-spacing-7));
                }
            }
        }

        &__card-title {
            @include text.getFontBySize(primary-650);
            color: var(--joy-color-neutral-70);
            margin-bottom: var(--joy-core-spacing-4);
            align-self: center;
        }

        &__card-jobs {
            ul {
                display: flex;
                flex-direction: column;
                align-items: center;
                overflow: hidden;
            }

            li,
            p {
                @include text.getFontBySize(primary-300);
                list-style: none;
                background-color: var(--joy-color-neutral-0);
                letter-spacing: var(--joy-font-letter-spacing-tight);
                border-radius: var(--joy-core-radius-5);
                padding: var(--joy-core-spacing-1) var(--joy-core-spacing-2);
                color: var(--joy-color-neutral-60);

                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 1;
                -webkit-box-orient: vertical;

                &:not(:last-child) {
                    margin-bottom: var(--joy-core-spacing-3);
                }

                a {
                    cursor: pointer;
                    color: inherit;

                    @include mq.screen_sm(min) {
                        text-decoration: underline;
                        text-underline-position: under;
                        transition: color var(--joy-transition-duration-default) var(--joy-transition-timing-function);

                        &:hover {
                            color: var(--joy-color-neutral-70);
                        }
                    }
                }
            }
        }
    }
</style>
